import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, User, Wrench, Calendar, Clock, RotateCcw, X } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const Alerts = () => {
  const { user } = useContext(AuthContext);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);
  const [returnAssignmentId, setReturnAssignmentId] = useState(null);

  useEffect(() => {
    loadExpiring();
  }, []);

  const loadExpiring = async () => {
    try {
      const { data } = await axios.get(`${API}/assignments/expiring`);
      setExpiring(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyLevel = (days) => {
    if (days <= 0) return { color: 'destructive', label: 'Expiré' };
    if (days <= 7) return { color: 'destructive', label: 'Urgent' };
    if (days <= 30) return { color: 'secondary', label: 'Bientôt' };
    return { color: 'outline', label: '2 mois' };
  };

  const handleReturn = async (assignmentId) => {
    setReturnAssignmentId(assignmentId);
    setConfirmReturnOpen(true);
  };

  const confirmReturn = async () => {
    if (!returnAssignmentId) return;
    try {
      await axios.post(`${API}/assignments/${returnAssignmentId}/return`);
      toast.success('Outil retourné avec succès - Alerte supprimée');
      loadExpiring();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du retour');
    } finally {
      setConfirmReturnOpen(false);
      setReturnAssignmentId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
          Alertes et Notifications
        </h1>
        <p className="text-muted-foreground mt-2">
          Affectations arrivant à échéance dans les 2 prochains mois
        </p>
      </div>

      {expiring.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <CardTitle className="font-heading text-destructive">
                {expiring.length} Alerte{expiring.length > 1 ? 's' : ''} Active{expiring.length > 1 ? 's' : ''}
              </CardTitle>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {expiring.map((assignment) => {
          const daysRemaining = getDaysRemaining(assignment.end_date);
          const urgency = getUrgencyLevel(daysRemaining);

          return (
            <Card
              key={assignment.id}
              data-testid={`alert-card-${assignment.id}`}
              className="hover:shadow-lg transition-shadow duration-200 border-2 border-destructive"
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-heading">Location Expirante</CardTitle>
                  <Badge variant={urgency.color} data-testid={`urgency-badge-${assignment.id}`}>
                    {urgency.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {assignment.collaborator?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{assignment.collaborator?.name}</span>
                    </div>
                    {assignment.collaborator?.email && (
                      <p className="text-sm text-muted-foreground">{assignment.collaborator.email}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{assignment.tool?.name}</span>
                  <span className="text-sm text-muted-foreground">({assignment.tool?.type})</span>
                </div>

                <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Date de fin:</span>
                    </div>
                    <span className="font-medium">{formatDate(assignment.end_date)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Temps restant:</span>
                    </div>
                    <Badge variant="outline" className="font-bold">
                      {daysRemaining !== null && (
                        daysRemaining > 0 ? `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''}` : 'Expiré'
                      )}
                    </Badge>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p>Affecté le {formatDate(assignment.assigned_date)}</p>
                </div>

                {user?.role === 'admin' && (
                  <div className="pt-2">
                    <Button
                      data-testid={`return-alert-${assignment.id}`}
                      onClick={() => handleReturn(assignment.id)}
                      variant="outline"
                      className="w-full rounded-full"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Retourner l'outil
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {expiring.length === 0 && (
        <Card className="border-2 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-heading font-medium mb-2">Aucune alerte active</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Aucune affectation n'arrive à échéance dans les 2 prochains mois.
              Vous serez notifié ici lorsque des affectations approchent de leur date de fin.
            </p>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirmReturnOpen}
        onOpenChange={setConfirmReturnOpen}
        onConfirm={confirmReturn}
        title="Retourner l'outil"
        description="Êtes-vous sûr de vouloir retourner cet outil ? L'alerte sera supprimée et l'outil sera ajouté au stock disponible."
      />
    </div>
  );
};

export default Alerts;